import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { syncPMEToFinancial } from '../utils/moduleSync';

export interface PMEInvoice {
  id: string;
  user_id: string;
  client_name: string;
  amount_ht: number;
  invoice_date: string;
  expected_payment_date: string;
  status: 'pending' | 'paid';
  probability: number;
  notes: string;
  created_at: string;
  updated_at: string;
}

export type PMEInvoiceInsert = Omit<PMEInvoice, 'id' | 'user_id' | 'created_at' | 'updated_at'>;

export function usePMEInvoices() {
  const { effectiveUserId } = useAuth();
  const [invoices, setInvoices] = useState<PMEInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!effectiveUserId) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('pme_invoices')
      .select('*')
      .eq('user_id', effectiveUserId)
      .order('expected_payment_date', { ascending: true });

    if (err) {
      setError(err.message);
    } else {
      setInvoices(data || []);
    }
    setLoading(false);
  }, [effectiveUserId]);

  useEffect(() => {
    load();
  }, [load]);

  async function addInvoice(invoice: PMEInvoiceInsert) {
    if (!effectiveUserId) return;
    const { error: err } = await supabase
      .from('pme_invoices')
      .insert({ ...invoice, user_id: effectiveUserId });
    if (err) throw new Error(err.message);
    await load();
    syncPMEToFinancial(effectiveUserId);
  }

  async function updateInvoice(id: string, updates: Partial<PMEInvoiceInsert>) {
    const { error: err } = await supabase
      .from('pme_invoices')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) throw new Error(err.message);
    await load();
    syncPMEToFinancial(effectiveUserId!);
  }

  async function deleteInvoice(id: string) {
    const { error: err } = await supabase
      .from('pme_invoices')
      .delete()
      .eq('id', id);
    if (err) throw new Error(err.message);
    await load();
    syncPMEToFinancial(effectiveUserId!);
  }

  async function markAsPaid(id: string) {
    await updateInvoice(id, { status: 'paid' });
  }

  const pendingInvoices = invoices.filter(i => i.status === 'pending');
  const totalPending = pendingInvoices.reduce(
    (sum, i) => sum + i.amount_ht * (i.probability / 100),
    0
  );

  return {
    invoices,
    pendingInvoices,
    totalPending,
    loading,
    error,
    addInvoice,
    updateInvoice,
    deleteInvoice,
    markAsPaid,
    refresh: load,
  };
}
